import { useEffect, useRef } from 'react';
import useScopedTranslation from 'hooks/useScopedTranslation';
import { textAreaI18n } from './i18n';
import './TextArea.scss';

export default function TextArea({
  label,
  placeholder,
  value,
  onChange,
  rows = 4,
  maxLength,
  disabled = false,
  required = false,
  error, // string | undefined
  helper, // string | undefined
  name,
  id,
  autoGrow = false, // auto height based on content
  showCount = false, // show "N / maxLength"
  endIcon,
  onEndIconClick,
  EndIconButton, // IconButton component from Design System
  className = '',
  ...rest
}) {
  const { t } = useScopedTranslation(textAreaI18n);
  const textareaRef = useRef(null);
  const textareaId = id || name || `ta_${Math.random().toString(36).slice(2)}`;
  const describedById = helper || error ? `${textareaId}__desc` : undefined;

  // Auto-grow height
  useEffect(() => {
    if (!autoGrow || !textareaRef.current) return;
    const el = textareaRef.current;
    el.style.height = ''; // reset
    el.style.height = `${el.scrollHeight}px`;
  }, [value, autoGrow]);

  const hasError = Boolean(error);
  const descText = hasError ? error : helper;

  return (
    <div
      className={`text-field ta ${hasError ? 'has-error' : ''} ${
        disabled ? 'is-disabled' : ''
      } ${className}`}
    >
      {label && (
        <label htmlFor={textareaId} className='text-field__label'>
          {label}{' '}
          {required && (
            <span className='text-field__req' aria-hidden='true'>
              *
            </span>
          )}
        </label>
      )}

      <div className='text-field__control ta__control'>
        <textarea
          id={textareaId}
          ref={textareaRef}
          className='text-field__input ta__input'
          placeholder={placeholder}
          value={value ?? ''}
          onChange={onChange}
          rows={rows}
          maxLength={maxLength}
          disabled={disabled}
          aria-invalid={hasError || undefined}
          aria-describedby={describedById}
          {...rest}
        />

        {EndIconButton && endIcon && (
          <EndIconButton
            ariaLabel={t('textarea_send')}
            icon={endIcon}
            size='sm'
            className='ta__end-icon-btn'
            onClick={onEndIconClick}
          />
        )}

        <div className='d-flex justify-content-between align-items-center mb-2'>
          {descText && (
            <div
              id={describedById}
              className={`text-field__desc ${hasError ? 'is-error' : ''}`}
            >
              {descText}
            </div>
          )}
          {showCount && maxLength ? (
            <div className='ta__counter caption'>
              {value?.length ?? 0}/{maxLength}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
